import { type ReactNode, useEffect, useState } from "react";
import { ErrorBoundary } from "@/shared/ui";
import { APPUNTAMENTI, type Appuntamento } from "./automatismi";

// Pezzi condivisi fra Impostazioni e Istruzioni (ridisegno 28/10/2026).

export type Tono = "ok" | "errore" | "attenzione" | "info" | "spento";

const TONO_PILL: Record<Tono, string> = {
  ok: "bg-green-soft",
  errore: "bg-red-soft",
  attenzione: "bg-amber-soft",
  info: "bg-blue-soft",
  spento: "bg-paper-2",
};
const TONO_PUNTO: Record<Tono, string> = {
  ok: "bg-green",
  errore: "bg-red",
  attenzione: "bg-amber",
  info: "bg-blue",
  spento: "bg-ink-4",
};

/** Pillola di stato: colore dal tono, testo sempre nero (regola di Davide). */
export function Pillola({ tono, children }: { tono: Tono; children: ReactNode }) {
  return (
    <span
      className={`inline-flex h-6 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-[12px] font-bold text-ink max-md:h-7 max-md:text-[13px] ${TONO_PILL[tono]}`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${TONO_PUNTO[tono]} ${tono === "info" ? "animate-pulse" : ""}`}
      />
      {children}
    </span>
  );
}

/** Una sezione della pagina: titolo, una riga di spiegazione, contenuto.
 *  Il boundary e' per sezione (incidente 28/05: una card rotta svuotava
 *  l'intera pagina); le altre restano usabili. */
export function Sezione({
  id,
  titolo,
  sottotitolo,
  azioni,
  children,
}: {
  id: string;
  titolo: string;
  sottotitolo?: ReactNode;
  azioni?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-titolo`} className="scroll-mt-4 max-md:scroll-mt-16">
      <div className="mb-3 flex items-end justify-between gap-4 max-md:flex-col max-md:items-start max-md:gap-2">
        <div className="min-w-0">
          <h2
            id={`${id}-titolo`}
            className="font-display text-[22px] font-bold leading-tight tracking-[-0.01em] text-ink max-md:text-[21px]"
          >
            {titolo}
          </h2>
          {sottotitolo && (
            <p className="mt-1 max-w-[70ch] text-[13px] leading-snug text-ink-2 max-md:text-[15px] max-md:text-ink">
              {sottotitolo}
            </p>
          )}
        </div>
        {azioni && <div className="flex shrink-0 flex-wrap items-center gap-2">{azioni}</div>}
      </div>
      <ErrorBoundary label={titolo}>{children}</ErrorBoundary>
    </section>
  );
}

/** Il foglio bianco su cui stanno righe e moduli. Mai uno dentro l'altro. */
export function Foglio({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-lg border border-line bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)] max-md:rounded-2xl ${className}`}
    >
      {children}
    </div>
  );
}

/** Interruttore acceso/spento. ``label`` e' il nome accessibile: il pulsante
 *  contiene solo il pallino, e senza il lettore di schermo direbbe solo
 *  «pulsante, premuto». */
export function Interruttore({
  acceso,
  label,
  describedBy,
  disabled = false,
  onClick,
}: {
  acceso: boolean;
  label: string;
  describedBy?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={acceso}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={onClick}
      className={[
        "relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-wait disabled:opacity-50 max-md:h-7 max-md:w-12",
        acceso ? "bg-green" : "bg-line-2",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-out max-md:h-6 max-md:w-6",
          acceso ? "translate-x-5" : "translate-x-0",
        ].join(" ")}
      />
    </button>
  );
}

/** Tasto secondario a pillola, la stessa forma in tutta la pagina. */
export const TASTO =
  "inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border border-line-2 bg-card px-3.5 text-[12.5px] font-semibold text-ink transition-[border-color,background-color,transform] duration-150 hover:border-ink-3 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4 disabled:cursor-not-allowed disabled:opacity-45 max-md:h-11 max-md:px-4 max-md:text-[14px]";

/** Ora corrente, rinfrescata ogni minuto: serve alla linea del tempo. */
function useAdesso(): Date {
  const [adesso, setAdesso] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setAdesso(new Date()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  return adesso;
}

type Tappa = { ora: number; minuti: number; nomi: string[] };

function raggruppa(appuntamenti: Appuntamento[]): Tappa[] {
  const perOra = new Map<string, Tappa>();
  for (const a of appuntamenti) {
    const minuti = a.minuti ?? 0;
    const k = `${a.ora}:${minuti}`;
    const t = perOra.get(k) ?? { ora: a.ora, minuti, nomi: [] };
    if (!t.nomi.includes(a.nome)) t.nomi.push(a.nome);
    perOra.set(k, t);
  }
  return [...perOra.values()].sort((a, b) => a.ora * 60 + a.minuti - (b.ora * 60 + b.minuti));
}

const hhmm = (ora: number, minuti: number) =>
  `${String(ora).padStart(2, "0")}:${String(minuti).padStart(2, "0")}`;

/** La giornata degli automatismi: gli orari fissi su una linea 0-24, con
 *  «adesso» e il prossimo appuntamento in evidenza. ``accese`` filtra le
 *  tappe delle lane spente; senza, mostra tutto (pagina Istruzioni). */
export function GiornataAutomatismi({ accese }: { accese?: Set<string> }) {
  const adesso = useAdesso();
  const tappe = raggruppa(APPUNTAMENTI.filter((a) => !accese || accese.has(a.lane)));
  const minutoOra = adesso.getHours() * 60 + adesso.getMinutes();
  const prossima = tappe.find((t) => t.ora * 60 + t.minuti > minutoOra) ?? tappe[0];
  const pos = (m: number) => `${(m / 1440) * 100}%`;

  if (tappe.length === 0) {
    return (
      <p className="px-5 py-4 text-[13px] text-ink-2">Nessun automatismo a orario fisso acceso.</p>
    );
  }

  return (
    <div>
      {prossima && (
        <p className="px-5 pt-4 text-[13px] text-ink max-md:px-4 max-md:text-[15px]">
          Prossimo:{" "}
          <strong className="font-bold">
            {prossima.nomi.join(" e ")} alle {hhmm(prossima.ora, prossima.minuti)}
          </strong>
          {prossima.ora * 60 + prossima.minuti <= minutoOra && " di domani"}
        </p>
      )}

      {/* Computer: la linea del tempo. Le etichette si alternano sopra e
          sotto la linea, cosi' due orari vicini (3 e 4, 18 e 19) non si
          coprono. */}
      <div className="relative mx-5 mb-2 mt-1 h-[132px] max-md:hidden" aria-hidden="true">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line-2" />
        <div
          className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-ink/80"
          style={{ width: pos(minutoOra) }}
        />
        {tappe.map((t, i) => {
          const sopra = i % 2 === 0;
          const m = t.ora * 60 + t.minuti;
          const passata = m <= minutoOra;
          const evidenza = t === prossima;
          const bordo = m < 90 ? "left" : m > 1350 ? "right" : "center";
          return (
            <div key={`${t.ora}:${t.minuti}`} className="absolute top-1/2" style={{ left: pos(m) }}>
              <span
                className={[
                  "absolute left-0 top-0 block h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
                  evidenza
                    ? "border-accent bg-accent shadow-[0_0_0_4px_rgb(var(--om-accent-soft))]"
                    : passata
                      ? "border-ink bg-ink"
                      : "border-ink-3 bg-card",
                ].join(" ")}
              />
              <span
                className={[
                  "absolute w-[108px] text-[11.5px] leading-tight",
                  sopra ? "bottom-3" : "top-3",
                  bordo === "left"
                    ? "left-0 -translate-x-1.5 text-left"
                    : bordo === "right"
                      ? "right-0 translate-x-1.5 text-right"
                      : "left-0 -translate-x-1/2 text-center",
                  passata && !evidenza ? "text-ink-2" : "text-ink",
                ].join(" ")}
              >
                <strong className="block font-bold tabular-nums">{hhmm(t.ora, t.minuti)}</strong>
                {t.nomi.join(" · ")}
              </span>
            </div>
          );
        })}
      </div>

      {/* Telefono e lettori di schermo: la stessa giornata come elenco. Sul
          telefono solo quello che deve ancora succedere: il passato e' gia'
          nello stato degli automatismi. */}
      <ol className="px-5 pb-4 pt-2 md:sr-only max-md:px-4">
        {(tappe.some((t) => t.ora * 60 + t.minuti > minutoOra)
          ? tappe.filter((t) => t.ora * 60 + t.minuti > minutoOra)
          : tappe
        ).map((t) => {
          const m = t.ora * 60 + t.minuti;
          const evidenza = t === prossima;
          return (
            <li
              key={`${t.ora}:${t.minuti}`}
              className="flex items-baseline gap-3 border-t border-dashed border-line py-2 text-[15px] first:border-t-0"
            >
              <span
                className={`w-12 shrink-0 font-bold tabular-nums ${evidenza ? "text-accent-2" : "text-ink"}`}
              >
                {hhmm(t.ora, t.minuti)}
              </span>
              <span className="text-ink">
                {t.nomi.join(" · ")}
                {m <= minutoOra && <span className="sr-only"> (gia' passato oggi)</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Indice delle sezioni: colonna a sinistra sul computer, fila che scorre
 *  in cima sul telefono. ``attivo`` lo tiene lo scroll della pagina. */
export function Indice({
  voci,
  attivo,
  onScegli,
  etichetta,
}: {
  voci: { id: string; nome: string; segnale?: ReactNode }[];
  attivo: string;
  onScegli: (id: string) => void;
  etichetta: string;
}) {
  return (
    <nav
      aria-label={etichetta}
      className="sticky top-0 self-start pt-1 max-md:z-20 max-md:-mx-3 max-md:-mt-3 max-md:flex max-md:gap-2 max-md:self-stretch max-md:overflow-x-auto max-md:border-b max-md:border-line max-md:bg-paper/95 max-md:px-3 max-md:py-2 max-md:backdrop-blur-sm max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden"
    >
      {voci.map((v) => {
        const qui = v.id === attivo;
        return (
          <a
            key={v.id}
            href={`#${v.id}`}
            aria-current={qui ? "location" : undefined}
            onClick={() => onScegli(v.id)}
            className={[
              "flex items-center justify-between gap-2 rounded-md px-3 py-[7px] text-[13.5px] no-underline transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-4",
              "max-md:h-10 max-md:shrink-0 max-md:whitespace-nowrap max-md:rounded-full max-md:border max-md:px-4 max-md:text-[14px] max-md:font-semibold",
              qui
                ? "bg-card font-bold text-ink shadow-[0_1px_2px_rgba(31,27,22,0.06)] max-md:border-ink max-md:bg-ink max-md:text-on-ink"
                : "font-medium text-ink-2 hover:bg-card/70 hover:text-ink max-md:border-line-2 max-md:bg-card max-md:text-ink",
            ].join(" ")}
          >
            <span>{v.nome}</span>
            {v.segnale}
          </a>
        );
      })}
    </nav>
  );
}

/** Quale sezione si sta leggendo, dallo scroll del contenitore. */
export function useSezioneVisibile(
  ids: string[],
  iniziale: string,
): [string, (id: string) => void] {
  const [attivo, setAttivo] = useState(iniziale);
  const chiave = ids.join(",");
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visibile = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visibile?.target.id) setAttivo(visibile.target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );
    for (const id of chiave.split(",")) {
      const nodo = document.getElementById(id);
      if (nodo) observer.observe(nodo);
    }
    return () => observer.disconnect();
  }, [chiave]);
  return [attivo, setAttivo];
}
